import { gql, useQuery } from "urql";
import type { TypedDocumentNode } from "urql";
import type { KpiComparisonMode } from "@acto/gql/graphql";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import { type CorporateGroupEnvelope, firstCorporateGroup } from "./corporateGroupEnvelope";

interface OthersKpiValue {
  totalValue: number;
  previousTotalValue: number;
  percentageChange: number | null;
}

export interface GroupOthersAggregate {
  count: number;
  revenue: OthersKpiValue;
  profit: OthersKpiValue;
  orders: OthersKpiValue;
}

type GroupOthersQueryData = CorporateGroupEnvelope<{ id: string; others: GroupOthersAggregate | null }>;

interface GroupOthersQueryVariables {
  id: string;
  dateRange: { startDate: string; endDate: string };
  comparisonMode: KpiComparisonMode;
}

const GroupOthersQuery: TypedDocumentNode<GroupOthersQueryData, GroupOthersQueryVariables> = gql`
  query GetCorporateGroupOthers($id: ID!, $dateRange: DateRangeInput!, $comparisonMode: KpiComparisonMode!) {
    clients(first: 1, filter: { id: $id }) {
      edges {
        node {
          id
          corporateGroup {
            id
            others(dateRange: $dateRange, comparisonMode: $comparisonMode) {
              count
              revenue {
                totalValue
                previousTotalValue
                percentageChange
              }
              profit {
                totalValue
                previousTotalValue
                percentageChange
              }
              orders {
                totalValue
                previousTotalValue
                percentageChange
              }
            }
          }
        }
      }
    }
  }
`;

export interface GroupOthersResult {
  /** Aggregate over the members the viewer cannot access; undefined while loading or when all members are accessible. */
  others: GroupOthersAggregate | undefined;
  isPending: boolean;
}

export function useGroupOthers(
  clientId: string | undefined,
  dateRange: { startDate: string; endDate: string },
): GroupOthersResult {
  const comparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());
  const [result] = useQuery({
    query: GroupOthersQuery,
    variables: { id: clientId ?? "", dateRange, comparisonMode },
    requestPolicy: "cache-first",
    pause: !clientId,
  });

  return {
    others: firstCorporateGroup(result.data)?.others ?? undefined,
    isPending: result.fetching || result.stale,
  };
}
